import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo, LogoMark } from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Globe } from "lucide-react";

const RIGHT_IMG = "https://images.unsplash.com/photo-1589936009537-bda4341d578f?w=1400&q=80";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

function GoogleButton({ label, testid }) {
  return (
    <button
      type="button"
      onClick={startGoogleAuth}
      className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-[#B6B09F] bg-white hover:border-[#0A0A0A] hover:shadow-md transition-all text-sm font-medium"
      data-testid={testid}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {label}
    </button>
  );
}

function Divider({ label }) {
  return (
    <div className="flex items-center gap-4 my-6">
      <div className="flex-1 h-px bg-[#B6B09F]/40" />
      <span className="text-xs font-mono-aq tracking-[0.2em] uppercase text-[#B6B09F]">{label}</span>
      <div className="flex-1 h-px bg-[#B6B09F]/40" />
    </div>
  );
}

function AuthShell({ title, children, footer, testid }) {
  const { t, lang, setLang } = useLang();
  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#F2F2F2]" data-testid={testid}>
      <div className="flex flex-col p-8 md:p-14">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="auth-home"><Logo /></Link>
          <button
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            className="flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.18em] font-mono-aq"
            data-testid="auth-lang-switcher"
          >
            <Globe size={14} /> {lang.toUpperCase()}
          </button>
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter leading-tight">{title}</h1>
            <div className="mt-10">{children}</div>
            <div className="mt-8 text-sm text-[#4A4A4A]">{footer}</div>
          </div>
        </div>
        <div className="font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F]">© Aqion</div>
      </div>
      <div className="hidden md:block relative bg-[#0A0A0A] overflow-hidden">
        <img src={RIGHT_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#EAE4D5]">
          <LogoMark size={56} />
          <div>
            <div className="label-eyebrow text-[#B6B09F]">— {t.auth.side_eyebrow}</div>
            <p className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight max-w-md">
              {t.auth.side_text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success(t.auth.login_success);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t.auth.login_title} testid="login-page" footer={<>{t.auth.no_account} <Link to="/register" className="aq-link font-medium">{t.auth.to_register}</Link></>}>
      <GoogleButton label={t.auth.google} testid="login-google" />
      <Divider label={t.auth.or} />
      <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
        <Field label={t.auth.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="login-email" required />
        <Field label={t.auth.password} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="login-password" required />
        <div className="flex justify-end -mt-2">
          <Link to="/forgot-password" className="text-xs text-[#4A4A4A] hover:text-[#0A0A0A] aq-link" data-testid="login-forgot-link">{t.auth.forgot}</Link>
        </div>
        <button type="submit" disabled={loading} className="aq-button-primary w-full" data-testid="login-submit">
          {loading ? "..." : t.auth.login_btn} <ArrowRight size={14} />
        </button>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(t.auth.register_success);
      const params = new URLSearchParams(window.location.search);
      const plan = params.get("plan");
      navigate(plan ? `/dashboard?plan=${plan}` : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t.auth.register_title} testid="register-page" footer={<>{t.auth.have_account} <Link to="/login" className="aq-link font-medium">{t.auth.to_login}</Link></>}>
      <GoogleButton label={t.auth.google} testid="register-google" />
      <Divider label={t.auth.or} />
      <form onSubmit={onSubmit} className="space-y-6" data-testid="register-form">
        <Field label={t.auth.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="register-name" required />
        <Field label={t.auth.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="register-email" required />
        <Field label={t.auth.password} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="register-password" required minLength={6} />
        <button type="submit" disabled={loading} className="aq-button-primary w-full" data-testid="register-submit">
          {loading ? "..." : t.auth.register_btn} <ArrowRight size={14} />
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, minLength }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        minLength={minLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-3 text-base text-[#0A0A0A] transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
